import { useParams } from "react-router-dom";
import { Layout } from "../components/layout/Layout";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { Button } from "../components/common/Button";
import { AdBlock } from "../components/ads/AdBlock";
// import { AdBanner } from "../components/ads/AdBanner";
import { Star, Tag, PenSquare } from "lucide-react";
import { tmdbApi } from "../services/tmdb";
import { useSelector } from "react-redux";
import { RootState } from "../store";
 
import { useQuery } from "@tanstack/react-query";
import { CastSection } from "../components/media/CastSection";
import VideoSection from "../components/media/VideoSection";
import { MovieSuggestions } from "../components/media/MovieSuggestions";
import { SEOHead } from "../components/seo/SEOHead";
import { buildSEOTitle } from "../utils/seo";
import {
  DetailContainer,
  BackdropImage,
  Content,
  PosterContainer,
  InfoContainer,
  Title,
  MetaInfo,
  MetaItem,
  Overview,
  ActionButtons,
  GenresContainer,
  GenreTag,
} from "../styles/MediaDetailStyles";
import { useTranslation } from 'react-i18next';
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import HighPerformanceBanner from '../components/ads/HighPerformanceBanner';
import HilltopAd from '../components/ads/HilltopAd';
import Crxcr2PopIn from '../components/ads/Crxcr2PopIn';

import { store } from '../store';
import { Language } from '../types';

interface LastViewedItem {
  image: string;
  title: string;
}

interface MoviePerson {
  job?: string;
  name: string;
}

interface MovieActor {
  name: string;
}

interface MovieGenre {
  id: number;
  name: string;
}

const MovieDetail = () => {
  const { id: movieId } = useParams<{ id: string }>();
  
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { t } = useTranslation();
  
  // Scroll automatique vers le haut au chargement
  useScrollToTopOnMount();

  const { data: movieDetails, isLoading } = useQuery({
    queryKey: ["movie", movieId],
    queryFn: async () => {
      const details = await tmdbApi.getDetails("movie", parseInt(movieId || '0'));
      // Traduire le contenu personnalisÃ© si nÃ©cessaire
      if (details.custom_overview) {
  // Traduction dÃ©sactivÃ©e
}
      return details;
    },
    enabled: !!movieId,
  });

  useEffect(() => {
    if (movieDetails) {
      const lastViewed: LastViewedItem[] =
        JSON.parse(localStorage.getItem("lastViewedFiles") || "[]");
      const updatedViewed = [
        { image: movieDetails.poster_path, title: movieDetails.title },
        ...lastViewed.filter((item: LastViewedItem) => item.title !== movieDetails.title),
      ].slice(0, 4);
      localStorage.setItem("lastViewedFiles", JSON.stringify(updatedViewed));
    }
  }, [movieDetails]);

  if (isLoading || !movieDetails) return <LoadingSpinner />;

  const isAdmin = user?.role === 'admin';
  const releaseYear = new Date(movieDetails.release_date).getFullYear();
  const runtime = `${Math.floor(movieDetails.runtime / 60)}h ${movieDetails.runtime % 60}m`;
  const currentUrl = window.location.href;
  const seoTitle = buildSEOTitle({
    baseTitle: movieDetails.title,
    year: releaseYear,
    prefix: 'Critique de',
    hook: (movieDetails as any)?.tagline || 'Bande-annonce, casting et avis',
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movieDetails.title,
    description: movieDetails.overview,
    image: `https://image.tmdb.org/t/p/w500${movieDetails.poster_path}`,
    datePublished: movieDetails.release_date,
    director: movieDetails.credits?.crew?.find((person: MoviePerson) => person.job === "Director")
      ?.name,
    actor: movieDetails.credits?.cast?.slice(0, 5).map((actor: MovieActor) => ({
      "@type": "Person",
      name: actor.name,
    })),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: movieDetails.vote_average,
      ratingCount: movieDetails.vote_count,
      bestRating: "10",
      worstRating: "0",
    },
    duration: `PT${movieDetails.runtime}M`,
    genre: movieDetails.genres?.map((genre: MovieGenre) => genre.name),
  };

  // Détection mobile locale pour forcer le fallback overlay du postitial
  const isMobileViewport = (() => {
    try { return !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches; } catch { return window.innerWidth <= 768; }
  })();
  const isMobileUA = (() => {
    try { return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent); } catch { return false; }
  })();
  const forceMobileFallback = isMobileViewport || isMobileUA;

  const handleCreateBlogPost = () => {
    const blogData = {
      title: movieDetails.title,
      type: 'movie',
      tmdbId: movieDetails.id,
      year: releaseYear,
      image: `https://image.tmdb.org/t/p/original${movieDetails.backdrop_path}`,
      poster: `https://image.tmdb.org/t/p/w500${movieDetails.poster_path}`,
      overview: movieDetails.overview,
      rating: movieDetails.vote_average,
      genres: movieDetails.genres.map(g => g.name),
    };
    navigate('/blog/new', { state: { mediaData: blogData } });
  };

  return (
    <Layout $backdrop={movieDetails?.backdrop_path ? true : false}>
      <SEOHead
        title={seoTitle}
        description={movieDetails.overview}
        image={`https://image.tmdb.org/t/p/original${movieDetails.backdrop_path}`}
        type="movie"
        structuredData={structuredData}
      />

      {/* PublicitÃ© HighPerformance en haut */}
      <HighPerformanceBanner 
        adKey="2154f7f7df439957dfc24240cd37dd3c"
        width={728}
        height={90}
        id="hp-banner-movie-top"
        hideOnMobile
      />
      
      {/* PublicitÃ© HilltopAds */}
      <HilltopAd id="hilltop-movie-1" />

      <DetailContainer>
        <BackdropImage backdrop={movieDetails.backdrop_path} />
        <Content>
          <PosterContainer>
            <img
              src={`https://image.tmdb.org/t/p/w500${movieDetails.poster_path}`}
              alt={movieDetails.title}
            />
          </PosterContainer>

          <InfoContainer>
            <Title>{movieDetails.title}</Title>

            {movieDetails.genres && (
              <GenresContainer>
                {movieDetails.genres.map((genre: MovieGenre) => (
                  <GenreTag key={genre.id} to={`/browse?genre=${genre.id}`}>
                    <Tag size={16} />
                    {genre.name}
                  </GenreTag>
                ))}
              </GenresContainer>
            )}

            <MetaInfo>
              <MetaItem>
                <Star size={20} />
                {movieDetails.vote_average.toFixed(1)}/10
              </MetaItem>
              <MetaItem>{releaseYear}</MetaItem>
              <MetaItem>{runtime}</MetaItem>
              
            </MetaInfo>

            <Overview>
              {movieDetails.custom_overview || movieDetails.overview}
            </Overview>

            <AdBlock format="banner" />

            {isAdmin && (
              <ActionButtons>
                <Button
                  icon={<PenSquare size={20} />}
                  onClick={handleCreateBlogPost}
                  variant="secondary"
                >
                  {t('blog.createPost', 'Create Blog Post')}
                </Button>
              </ActionButtons>
            )}

            {/* Bandes-annonces et extraits */}
            <VideoSection videos={movieDetails.videos?.results || []} />

            {/* Distribution */}
            {movieDetails.credits?.cast && (
              <CastSection cast={movieDetails.credits.cast} />
            )}

            {/* Films similaires */}
            <MovieSuggestions movieId={movieDetails.id} />
          </InfoContainer>
        </Content>

        <AdBlock format="banner" />
      </DetailContainer>
      
      {/* PublicitÃ© HighPerformance en bas */}
      <HighPerformanceBanner 
        adKey="2154f7f7df439957dfc24240cd37dd3c"
        width={728}
        height={90}
        id="hp-banner-movie-bottom"
      />

      {/* CrackRevenue Postitial (desktop) / Fallback overlay (mobile) */}
      <Crxcr2PopIn 
        url="https://t.vlmai-3.com/408074/10046/0?aff_sub5=SF_006OG000004lmDN&aff_sub4=AT_0016"
        contentUrl="https://www.gptgirlfriend.online/?aff_id=1&transaction_id=postitial"
        postitialBehavior
        expireDays={0.01}
        forceFallback={forceMobileFallback}
      />
    </Layout>
  );
};

export default MovieDetail;

